import { useTranslation } from "react-i18next";
import {
  useDeleteMessageMutation,
  useDeleteMessagesMutation,
} from "hooks/useMessages";
import { showError } from "utils/index";
import { useChatContextStore } from "../providers/ChatContextProvider";

export const useMessageSelection = () => {
  const { t } = useTranslation("messages");
  const {
    discussion,
    setEditingMessage,
    selectedMessages,
    setSelectedMessages,
  } = useChatContextStore();

  const { mutateAsync: mutateDeleteAsync, isPending: isDeletePending } =
    useDeleteMessageMutation();
  const { mutateAsync: mutateDeleteManyAsync, isPending: isDeleteManyPending } =
    useDeleteMessagesMutation();
  const isDeleting = isDeletePending || isDeleteManyPending;

  const selectedMessage =
    selectedMessages.length === 1 ? selectedMessages[0] : null;

  const onEdit = () => {
    setEditingMessage(selectedMessage);
    setSelectedMessages([]);
  };

  const onCancelEdit = () => setEditingMessage(null);

  const onDeselect = () => setSelectedMessages([]);

  const onDelete = async () => {
    if (!discussion?.id) return;
    try {
      if (selectedMessages.length === 1 && selectedMessage?.id) {
        await mutateDeleteAsync({
          id: selectedMessage.id,
          discussionId: discussion.id,
        });
      } else if (selectedMessages.length > 1) {
        const ids = selectedMessages.map(m => m.id).filter(Boolean) as string[];
        await mutateDeleteManyAsync({ ids, discussionId: discussion.id });
      }
    } catch {
      return;
    }
    setSelectedMessages([]);
  };

  const onCopy = async () => {
    const text = [...selectedMessages]
      .sort(
        (a, b) =>
          new Date(a.createdTime ?? 0).getTime() -
          new Date(b.createdTime ?? 0).getTime()
      )
      .map(m => m.message)
      .filter(Boolean)
      .join("\n");
    if (text && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        showError(t("copyFailed"));
      }
    }
    setSelectedMessages([]);
  };

  return {
    isDeleting,
    onEdit,
    onCancelEdit,
    onDeselect,
    onDelete,
    onCopy,
  };
};
